// Frontend/src/lib/sseReconnectPolicy.ts
//
// The reconnect/backoff decision logic, kept separate from notificationStream.ts's
// actual fetch/stream I/O specifically so it can be unit tested without mocking
// a network stream -- see sseReconnectPolicy.test.ts.
//
// Three kinds of "the connection ended", three different responses:
//   - a clean cycle-end (the server closes every ~45s on purpose, see
//     backend/notifications/stream.py) is not a failure and reconnects
//     almost immediately, with a little jitter so many open tabs don't all
//     hit the server in the same instant.
//   - a real error (network drop, non-2xx, a broken stream) backs off
//     exponentially, so a server having a bad moment is not hammered by
//     every connected client retrying in lockstep.
//   - too many real errors in a row means SSE itself is not working right
//     now (the user's network, or the server) -- the caller should fall
//     back to REST polling rather than spinning on reconnect attempts
//     forever, and this policy periodically allows one more SSE attempt
//     rather than staying degraded permanently.

export type SseStatus = "connecting" | "open" | "reconnecting" | "degraded";

export interface SseReconnectPolicyOptions {
  /** Consecutive real failures before falling back to polling. */
  degradeAfterFailures?: number;
  /** Base delay for exponential backoff, in ms. */
  baseDelayMs?: number;
  /** Backoff never waits longer than this, in ms. */
  maxDelayMs?: number;
  /** How long to stay degraded before trying SSE again, in ms. */
  degradedCooldownMs?: number;
  /** Delay before reconnecting after a clean cycle-end, in ms. */
  cleanCloseDelayMs?: number;
  /** Injectable for deterministic tests; defaults to Math.random. */
  random?: () => number;
}

const DEFAULTS: Required<SseReconnectPolicyOptions> = {
  degradeAfterFailures: 4,
  baseDelayMs: 500,
  maxDelayMs: 30_000,
  degradedCooldownMs: 120_000,
  cleanCloseDelayMs: 250,
  random: Math.random,
};

export class SseReconnectPolicy {
  private opts: Required<SseReconnectPolicyOptions>;
  private consecutiveFailures = 0;
  private _status: SseStatus = "connecting";

  constructor(options: SseReconnectPolicyOptions = {}) {
    this.opts = { ...DEFAULTS, ...options };
  }

  get status(): SseStatus {
    return this._status;
  }

  /** The connection opened and at least stayed up long enough to matter. */
  recordSuccess(): void {
    this.consecutiveFailures = 0;
    this._status = "open";
  }

  /**
   * The server ended the stream on schedule (event: cycle-end). Not a
   * failure -- reconnect promptly.
   */
  recordCleanClose(): number {
    this.consecutiveFailures = 0;
    this._status = "reconnecting";
    const jitter = this.opts.random() * this.opts.cleanCloseDelayMs * 0.5;
    return Math.round(this.opts.cleanCloseDelayMs + jitter);
  }

  /**
   * A real failure: network error, non-2xx, a stream that broke mid-read.
   * Returns the delay (ms) before the next attempt, and moves to "degraded"
   * once too many have happened in a row.
   */
  recordFailure(): number {
    this.consecutiveFailures += 1;

    if (this.consecutiveFailures >= this.opts.degradeAfterFailures) {
      this._status = "degraded";
      return this.opts.degradedCooldownMs;
    }

    this._status = "reconnecting";
    const exponent = this.consecutiveFailures - 1;
    const raw = this.opts.baseDelayMs * 2 ** exponent;
    const capped = Math.min(raw, this.opts.maxDelayMs);
    // Full jitter: a random point between 0 and the capped delay, not a
    // fixed delay +/- a small amount -- this is what actually prevents
    // synchronized retries across many clients, per the standard
    // "exponential backoff with full jitter" approach.
    return Math.round(this.opts.random() * capped);
  }

  /** The degraded cooldown elapsed; try SSE again (still counts as "connecting", not "open"). */
  retryFromDegraded(): void {
    this._status = "connecting";
  }
}
